import { useState, useCallback, useRef } from 'react'
import {
  Upload,
  FileJson,
  X,
  Loader2,
  Check,
  AlertCircle,
  AlertTriangle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { usePreviewImport, useExecuteImport } from '@/hooks/use-policy-import'
import type {
  ImportPreview,
  ImportExecuteResult,
  PolicyExportBundle,
} from '@/types/policy-import-export'

type Step = 'upload' | 'preview' | 'results'

const MAX_FILE_SIZE = 5 * 1024 * 1024

interface PolicyImportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function PolicyImportDialog({
  open,
  onOpenChange,
}: PolicyImportDialogProps) {
  const [step, setStep] = useState<Step>('upload')
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [bundle, setBundle] = useState<PolicyExportBundle | null>(null)
  const [skipConflicts, setSkipConflicts] = useState(true)
  const [result, setResult] = useState<ImportExecuteResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const previewImport = usePreviewImport()
  const executeImport = useExecuteImport()

  const resetState = useCallback(() => {
    setStep('upload')
    setFile(null)
    setIsDragging(false)
    setPreview(null)
    setBundle(null)
    setSkipConflicts(true)
    setResult(null)
  }, [])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        resetState()
      }
      onOpenChange(nextOpen)
    },
    [onOpenChange, resetState]
  )

  const validateFile = useCallback((f: File): string | null => {
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf('.'))
    if (ext !== '.json') {
      return `Unsupported file type: ${ext}. Only .json files are accepted.`
    }
    if (f.size > MAX_FILE_SIZE) {
      return `File too large: ${formatFileSize(f.size)}. Maximum: 5 MB`
    }
    return null
  }, [])

  const handleFileSelect = useCallback(
    (f: File) => {
      const error = validateFile(f)
      if (error) {
        toast.error(error)
        return
      }
      setFile(f)
    },
    [validateFile]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      const droppedFile = e.dataTransfer.files[0]
      if (droppedFile) {
        handleFileSelect(droppedFile)
      }
    },
    [handleFileSelect]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0]
      if (selectedFile) {
        handleFileSelect(selectedFile)
      }
    },
    [handleFileSelect]
  )

  const handleUploadAndPreview = useCallback(async () => {
    if (!file) return

    try {
      const text = await file.text()
      const parsed = JSON.parse(text) as PolicyExportBundle
      setBundle(parsed)

      const previewResult = await previewImport.mutateAsync(file)
      setPreview(previewResult)
      setStep('preview')
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON file')
      }
    }
  }, [file, previewImport])

  const handleExecuteImport = useCallback(async () => {
    if (!bundle) return

    try {
      const importResult = await executeImport.mutateAsync({
        bundle,
        skipConflicts,
      })
      setResult(importResult)
      setStep('results')
    } catch {
      // Error toast handled by the hook
    }
  }, [bundle, skipConflicts, executeImport])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === 'upload' && 'Import Policies'}
            {step === 'preview' && 'Import Preview'}
            {step === 'results' && 'Import Results'}
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' &&
              'Upload a policy bundle (.json) exported from poli-x.'}
            {step === 'preview' &&
              'Review what will be imported. Conflicts will be skipped.'}
            {step === 'results' && 'Import completed. Review the results below.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div
              className={cn(
                'border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
                isDragging && 'border-primary bg-primary/5',
                !isDragging &&
                  !file &&
                  'border-muted-foreground/25 hover:border-primary/50',
                file && 'border-primary/50 bg-primary/5'
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleInputChange}
                className="hidden"
              />

              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileJson className="h-8 w-8 text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => {
                      e.stopPropagation()
                      setFile(null)
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="font-medium">
                    Drop a file here or click to browse
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Accepts .json files (max 5 MB)
                  </p>
                </>
              )}
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && preview && (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {preview.summary.newPolicies}
                  </p>
                  <p className="text-xs text-muted-foreground">New Policies</p>
                </div>
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-2xl font-bold text-yellow-600">
                    {preview.summary.conflictPolicies}
                  </p>
                  <p className="text-xs text-muted-foreground">Conflicts</p>
                </div>
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {preview.summary.newAttributes}
                  </p>
                  <p className="text-xs text-muted-foreground">New Attributes</p>
                </div>
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-2xl font-bold text-muted-foreground">
                    {preview.summary.existingAttributes}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Existing Attributes
                  </p>
                </div>
              </div>

              {/* Conflict warning */}
              {preview.summary.conflictPolicies > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20 p-3">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-yellow-800 dark:text-yellow-200">
                      {preview.summary.conflictPolicies} policy conflict(s) found
                    </p>
                    <p className="text-yellow-700 dark:text-yellow-300">
                      Conflicting policies will be skipped during import.
                    </p>
                  </div>
                </div>
              )}

              {/* Conflict details table */}
              {preview.summary.conflictPolicies > 0 && (
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Policy Name</TableHead>
                        <TableHead>Conflict</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.policies
                        .filter((p) => p.status === 'conflict')
                        .map((policy) => (
                          <TableRow key={policy.name}>
                            <TableCell className="font-medium">
                              {policy.name}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {policy.conflictDetails ?? 'Name already exists'}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* New policies list */}
              {preview.summary.newPolicies > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">
                    New policies to create ({preview.summary.newPolicies})
                  </p>
                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Policy Name</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.policies
                          .filter((p) => p.status === 'new')
                          .map((policy) => (
                            <TableRow key={policy.name}>
                              <TableCell className="font-medium">
                                {policy.name}
                              </TableCell>
                              <TableCell>
                                <Badge className="text-xs bg-green-100 text-green-800 hover:bg-green-100">
                                  New
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* New attributes */}
              {preview.summary.newAttributes > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">
                    New attributes to create ({preview.summary.newAttributes})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.attributes
                      .filter((a) => a.status === 'new')
                      .map((attr) => (
                        <Badge key={attr.key} variant="secondary" className="text-xs">
                          {attr.key} ({attr.category})
                        </Badge>
                      ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Step 3: Results */}
          {step === 'results' && result && (
            <>
              {/* Summary */}
              <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/50">
                {result.policiesCreated > 0 && (
                  <div className="flex items-center gap-1.5 text-sm">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>{result.policiesCreated} policies created</span>
                  </div>
                )}
                {result.attributesCreated > 0 && (
                  <div className="flex items-center gap-1.5 text-sm">
                    <Check className="h-4 w-4 text-blue-600" />
                    <span>{result.attributesCreated} attributes created</span>
                  </div>
                )}
                {result.hierarchiesCreated > 0 && (
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <span>{result.hierarchiesCreated} hierarchies created</span>
                  </div>
                )}
                {result.skipped > 0 && (
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <span>{result.skipped} skipped</span>
                  </div>
                )}
                {result.errors.length > 0 && (
                  <div className="flex items-center gap-1.5 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4" />
                    <span>{result.errors.length} error(s)</span>
                  </div>
                )}
              </div>

              {/* Errors list */}
              {result.errors.length > 0 && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-3">
                  <p className="text-sm font-medium text-destructive mb-1">Errors</p>
                  <ul className="text-xs text-destructive space-y-0.5">
                    {result.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          {step === 'upload' && (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUploadAndPreview}
                disabled={!file || previewImport.isPending}
              >
                {previewImport.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-1" />
                    Upload & Preview
                  </>
                )}
              </Button>
            </>
          )}

          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('upload')}>
                Back
              </Button>
              <Button
                onClick={handleExecuteImport}
                disabled={executeImport.isPending}
              >
                {executeImport.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Importing...
                  </>
                ) : (
                  'Import'
                )}
              </Button>
            </>
          )}

          {step === 'results' && (
            <Button onClick={() => handleOpenChange(false)}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
